import { defineStore } from 'pinia';
import { useCustomerStore } from './customer';
import customerApi from '@api/customer';
import { useRouter } from 'vue-router';

export const useOrderStore = defineStore('order', () => {
    const router = useRouter();
    const userStore = useUserStore();
    const moveStore = useMoveStore();

    const loading = ref(false);
    const relatedOrdersLoading = ref(false);
    const data = ref({
        customer: null,
        items: [],
    });
    const predefined = ref({
        agents: [],
    });
    const mapData = ref({});
    const bookingLink = ref('');

    const getCustomer = computed(() => data.value.customer);
    const getCustomerId = computed(() => data.value.customer?.id ?? null);
    const getCustomerName = computed(() => data.value.customer?.fullname);
    const getCustomerEmail = computed(() => data.value.customer?.email);
    const getItems = computed(() => data.value.items);
    const getOrderId = computed(() => data.value.id);
    const getOrderRef = computed(() => data.value.ref);
    const getAssignee = computed(() => data.value.agent);
    const getStatus = computed(() => data.value.status);
    const getStatusFormatted = computed(() => data.value.status_formatted);
    const getHeat = computed(() => data.value.heat);

    const isPendingBankDepositUnconfirmed = computed(() =>
        data.value.items?.some((item) => item.is_pending_bank_deposit && !item.is_confirmed),
    );
    const isPendingBankDepositConfirmed = computed(() =>
        data.value.items?.some((item) => item.is_pending_bank_deposit && item.is_confirmed),
    );
    const isPendingApproval = computed(() => data.value.items?.some((item) => item.is_pending_approval));
    const isDraft = computed(() => data.value.status === 'DRAFT');
    const isConfirmed = computed(() => ['JOB', 'JOB_PARTIAL', 'JOB_CLOSED'].includes(data.value?.state));
    const isTrashed = computed(() => data.value?.state === 'BIN');
    const isSafeToEmail = computed(() => data.value?.safe_to_email);
    const isSafeToSMS = computed(() => data.value?.safe_to_sms);
    const isSafeToCall = computed(() => data.value?.safe_to_call);

    const canActionOrder = computed(
        () =>
            userStore.hasAnyRole(['SUPER', 'ADMIN', 'OPS', 'OWNER', 'MANAGER', 'SALES']) ||
            getAssignee.value ||
            isConfirmed.value,
    );

    // Determine if the user is assigned to the order so they can access
    // the email and sms buttons. Admin roles can use the comms regardless
    // whether they are assigned to the order or not. SALES role can use the
    // comms regardless whether they are assigned to the order or not.
    const canSendComms = computed(
        () =>
            userStore.hasAnyRole(['SUPER', 'ADMIN', 'OPS', 'OWNER', 'MANAGER', 'SALES']) ||
            (getAssignee.value && getAssignee.value.id === userStore.getUserId),
    );

    function setOrderId(orderId) {
        data.value.id = orderId;
    }

    async function update(payload, orderId = getOrderId.value) {
        try {
            loading.value = true;
            const { data } = await window.tenantAxios.patch(`/orders/${orderId}`, payload);

            mapResponseValues(data.order);

            return data.order;
        } catch (error) {
            loading.value = false;

            throw error;
        } finally {
            loading.value = false;
        }
    }

    // Retrieve the order based on the orderId.
    async function retrieve(orderId = getOrderId.value) {
        try {
            loading.value = true;
            const { data: respData } = await window.tenantAxios.get(`/orders/${orderId}`);

            data.value = {
                ...data.value,
                ...respData.order,
            };
        } catch (error) {
            // If the order is not found, redirect to the 404 page instead of
            // displaying a page with skeleton loaders.
            loading.value = false;
            if (error.code === 404) {
                router.replace({ name: 'OrderNotFound' });
            }
        } finally {
            loading.value = false;
        }
    }

    async function declineOffer(offer, status, orderId = getOrderId.value) {
        const resp = await window.tenantAxios.post(`/orders/${orderId}/decline-offer`, {
            offer,
            status,
        });
        data.value.predefined.offers = resp.data.offers;

        return resp.data.offers;
    }

    async function addServiceShell(offer, orderId = getOrderId.value) {
        await window.tenantAxios.post(`/orders/${orderId}/accept-offer`, {
            offer,
        });
    }

    async function fetchItems(orderId = getOrderId.value) {
        try {
            loading.value = true;

            const { data: respData } = await window.tenantAxios.get(`/orders/${orderId}/items`);

            data.value.items = respData.data;

            data.value.items.forEach((item) => {
                switch (item.productable_type) {
                    case 'move':
                        moveStore.setMoveId(item.productable_id);
                }
            });

            return respData;
        } finally {
            loading.value = false;
        }
    }

    async function getTotals(orderId = getOrderId.value) {
        const { data: resp } = await window.tenantAxios.get(`/orders/${orderId}/totals`);

        data.value.price_total = resp.price_total;
        data.value.card_fee_total = resp.card_fee_total;
        data.value.price_sub_total = resp.price_sub_total;
        data.value.price_owing = resp.price_owing;
        data.value.payment_total = resp.payment_total;
        data.value.payment_sub_total = resp.payment_sub_total;

        resp.items.forEach((item, itemIndex) => {
            data.value.items[itemIndex] = {
                ...data.value.items[itemIndex],
                ...item,
            };
        });
    }

    async function getRouteData(orderId = getOrderId.value) {
        const resp = await window.tenantAxios.get(`/orders/${orderId}/route`);

        mapData.value = resp.data.route;
    }

    async function fetchBookingLink() {
        try {
            loading.value = true;

            const { data } = await window.tenantAxios.get(`/orders/${getOrderId.value}/link`, {
                params: { preview: true },
            });

            bookingLink.value = data.link;

            return data.link;
        } finally {
            loading.value = false;
        }
    }

    async function retrieveRelatedOrders() {
        try {
            relatedOrdersLoading.value = true;

            const { data: respData } = await window.tenantAxios.get(`/orders/${this.getOrderId}/related-orders`);

            data.value = { ...data.value, related_orders: respData };

            return respData.link;
        } finally {
            relatedOrdersLoading.value = false;
        }
    }

    async function updateCustomer(customerId, customerData) {
        try {
            loading.value = true;

            const customerUpdateResponse = await customerApi.update(customerId, {
                ...customerData,
            });

            data.value.customer = customerUpdateResponse.data.customer;

            loading.value = false;

            return customerUpdateResponse.data.customer;
        } finally {
            loading.value = false;
        }
    }

    async function toggleHeat(heat = getHeat.value, orderId = getOrderId.value) {
        let value;

        if (heat === 'High') {
            value = 0;
        } else {
            value = 3;
        }

        const response = await window.tenantAxios.patch(`orders/${orderId}`, {
            order: {
                heat: value,
            },
        });

        data.value.heat = response.data.order.heat;
    }

    async function getConfirmables(orderId = getOrderId.value) {
        const {
            data: { data: response },
        } = await window.tenantAxios.get(`/orders/${orderId}/confirm`);

        // Check if the tenant has their Stripe account verified.
        if (response.stripe_verified === 1) {
            response.payment_methods.CREDIT_CARD = 'Credit Card';
        }

        return response;
    }

    async function getCompletables(orderId = getOrderId.value) {
        const {
            data: { data: response },
        } = await window.tenantAxios.get(`/orders/${orderId}/complete`);

        // Check if the tenant has their Stripe account verified.
        if (response.stripe_verified === 1) {
            response.payment_methods.CREDIT_CARD = 'Credit Card';
        }

        return response;
    }

    function mapResponseValues(values) {
        data.value.ref = values.ref;
        data.value.type = values.type;
        data.value.state = values.state;
        data.value.status = values.status;
        data.value.heat = values.heat;
        data.value.price_total = values.price_total;

        data.value.safe_to_email = values.safe_to_email;
        data.value.safe_to_sms = values.safe_to_sms;
        data.value.safe_to_call = values.safe_to_call;

        // Sync customer store
        const customer = useCustomerStore();
        customer.data = values.customer;

        data.value.agent = values.agent;
        data.value.created_at = values.created_at;
        data.value.updated_at = values.updated_at;
        data.value.meta = values.meta;
    }

    const getInsurancePolicyBrand = computed(() => data.value?.insurance_policy_brand);

    function getServiceLabel(serviceLabel) {
        if (serviceLabel === 'Insurance') {
            return getInsurancePolicyBrand.value;
        }

        return serviceLabel;
    }

    function reset() {
        loading.value = false;
        relatedOrdersLoading.value = false;
        data.value = {
            customer: null,
        };
        predefined.value = {
            agents: [],
        };
        mapData.value = {};
        bookingLink.value = '';
    }

    function canBeTrashed() {
        const userStore = useUserStore();

        if (data.value?.state != 'BIN') {
            if (userStore.hasAnyRole(['SUPER', 'OWNER', 'ADMIN', 'OPS'])) {
                // can do if admin or super regardless of status
                return true;
            } else if (!['PARTIALLY_CONFIRMED', 'CONFIRMED'].includes(data.value?.status)) {
                // agents can only bin assinged orders
                if (userStore.isAgent && getAssignee.value?.id != userStore.getUserId) {
                    return false;
                }
                // can bin regardless of role if order is not in these statuses
                return true;
            }
        }
        return false;
    }

    return {
        loading,
        relatedOrdersLoading,
        data,
        predefined,
        mapData,
        bookingLink,

        getCustomer,
        getCustomerId,
        getCustomerName,
        getCustomerEmail,
        getItems,
        getOrderId,
        getOrderRef,
        getAssignee,
        getStatus,
        getStatusFormatted,
        getHeat,
        isPendingBankDepositUnconfirmed,
        isPendingBankDepositConfirmed,
        isPendingApproval,
        isDraft,
        isConfirmed,
        isTrashed,
        isSafeToEmail,
        isSafeToSMS,
        isSafeToCall,
        canActionOrder,
        canSendComms,

        setOrderId,
        update,
        retrieve,
        declineOffer,
        addServiceShell,
        fetchItems,
        getTotals,
        getRouteData,
        fetchBookingLink,
        retrieveRelatedOrders,
        updateCustomer,
        toggleHeat,
        getConfirmables,
        getCompletables,
        getInsurancePolicyBrand,
        getServiceLabel,
        reset,
        canBeTrashed,
    };
});
